import { useEffect, useState } from 'react';
import { downloadCheque, getMyAgencyCommissions } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CalendarDays, Download, Loader2, Percent, ReceiptText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  EmptyState,
  KpiCard,
  formatDate,
  formatMoney,
  formatPercent,
  safeList,
} from './AgencyPortalShared';

export default function AgencyCommissions() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await getMyAgencyCommissions();
        if (!cancelled) setData(res.data || null);
      } catch (err) {
        console.error(err);
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const summary = data?.summary || {};
  const payments = safeList(data?.items);
  const schedule = safeList(data?.commissionSchedule);
  const cheques = safeList(data?.cheques);

  const handleDownloadCheque = async (cheque) => {
    try {
      setDownloadingId(cheque.id);
      const response = await downloadCheque(cheque.id);
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `MCC_Commission_Cheque_${cheque.payment_date || ''}.pdf`;
      document.body.appendChild(anchor); anchor.click(); anchor.remove(); URL.revokeObjectURL(url);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Commission cheque could not be downloaded.'); }
    finally { setDownloadingId(''); }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h2 className="text-2xl font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
          Commissions
        </h2>
        <p className="mt-1 text-sm text-[#062B1A]/60">Commission summary and recorded payments.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <KpiCard label="Total Paid" value={formatMoney(summary.total_paid)} icon={ReceiptText} accent />
        <KpiCard label="Paid This Year" value={formatMoney(summary.total_paid_current_year)} icon={CalendarDays} />
        <KpiCard label="Payments" value={summary.count || 0} icon={ReceiptText} />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm xl:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
              Payment History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((item) => (
                  <div key={item} className="h-16 animate-pulse rounded-lg bg-[#F7F8FA]" />
                ))}
              </div>
            ) : payments.length === 0 ? (
              <EmptyState icon={ReceiptText} title="No commission payments recorded" />
            ) : (
              <div className="overflow-hidden rounded-lg border border-[rgba(6,43,26,0.08)]">
                <div className="hidden grid-cols-[1fr_0.9fr_0.8fr_0.9fr_1fr] gap-4 bg-[#F7F8FA] px-4 py-3 text-xs font-semibold uppercase tracking-[0.08em] text-[#062B1A]/45 lg:grid">
                  <span>Student</span>
                  <span>Date</span>
                  <span>Amount</span>
                  <span>Method</span>
                  <span>Reference</span>
                </div>
                <div className="divide-y divide-[rgba(6,43,26,0.06)]">
                  {payments.map((payment) => (
                    <div key={payment.id} className="grid gap-3 px-4 py-4 lg:grid-cols-[1fr_0.9fr_0.8fr_0.9fr_1fr] lg:items-center lg:gap-4">
                      <p className="text-sm font-semibold text-[#062B1A]">{payment.student_name || 'Commission payment'}</p>
                      <p className="text-sm text-[#062B1A]/65">{formatDate(payment.payment_date)}</p>
                      <p className="text-sm font-semibold text-[#062B1A]">{formatMoney(payment.amount)}</p>
                      <p className="text-sm text-[#062B1A]/65">{payment.method || 'Not set'}</p>
                      <p className="truncate text-sm text-[#062B1A]/65">{payment.reference || 'Not set'}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
              <Percent className="h-4 w-4 text-[#D4AF37]" />
              Commission Schedule
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {schedule.length === 0 ? (
              <EmptyState icon={Percent} title="No schedule available" />
            ) : (
              <div className="overflow-hidden rounded-lg border border-[rgba(6,43,26,0.08)]">
                <div className="grid grid-cols-2 bg-[#F7F8FA] px-3 py-2 text-xs font-semibold uppercase tracking-[0.08em] text-[#062B1A]/45">
                  <span>Year</span>
                  <span>Percentage</span>
                </div>
                {schedule.map((row, index) => (
                  <div key={`${row.year}-${index}`} className="grid grid-cols-2 border-t border-[rgba(6,43,26,0.06)] px-3 py-2 text-sm text-[#062B1A]">
                    <span>{row.year}</span>
                    <span className="font-semibold">{formatPercent(row.percent)}</span>
                  </div>
                ))}
              </div>
            )}
            {data?.shortProgramCommissionPercent !== undefined && (
              <Badge variant="outline" className="border-[#D4AF37]/45 bg-[#D4AF37]/12 text-[#7A5A05]">
                Short programs: {formatPercent(data.shortProgramCommissionPercent)}
              </Badge>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
        <CardHeader className="pb-3"><CardTitle className="text-base font-semibold text-[#062B1A]">Commission Cheques</CardTitle></CardHeader>
        <CardContent>{cheques.length === 0 ? <EmptyState icon={ReceiptText} title="No commission cheques available" /> : <div className="divide-y overflow-hidden rounded-lg border">{cheques.map((cheque) => <div key={cheque.id} className="flex items-center justify-between gap-4 px-4 py-3"><div><p className="text-sm font-semibold text-[#062B1A]">{formatMoney(cheque.amount)}</p><p className="text-xs text-[#062B1A]/50">{formatDate(cheque.payment_date)}</p></div><Button variant="outline" size="sm" onClick={() => handleDownloadCheque(cheque)} disabled={downloadingId === cheque.id}>{downloadingId === cheque.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}Download cheque</Button></div>)}</div>}</CardContent>
      </Card>
    </div>
  );
}
